<script setup lang="ts">
import '@/assets/scss/form.scss'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import { useConfirm } from 'primevue'
import { useToast } from 'primevue/usetoast'
import { ref, onMounted, watch } from 'vue'
import { CAMPAIGN_API } from '../constants/linkedservice.api.uri.const'
import { schemaCampaign } from '../helper/schema'
import moment from 'moment'
import { numHelper } from '@/utils/numHelper.ts'

const confirm = useConfirm()

interface Props {
  id: any
  closeModal?: any
}

const props = defineProps<Props>()
const emit = defineEmits(['update:closeModal'])
const toast = useToast()
const formRef = ref()
const _date: any = ref(null)

const initialValues = ref({
  type: '',
  value: '',
  title: '',
  date: '',
})
const resolver = yupResolver(schemaCampaign)

const requestCreate = async (values: any) => {
  const data = {
    ...values,
    activated_at: moment(values.activated_at).format('YYYY-MM-DD'),
    expired_at: moment(values.expired_at).format('YYYY-MM-DD'),
  }
  try {
    const res = await APIClient.post(CAMPAIGN_API.create.replace(':id', props.id), data)
    if (res.data.code === 200) {
      //close modal
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Tạo chiến dịch thành công',
        life: 3000,
      })
      emit('update:closeModal')
      return
    }
  } catch (e) {
    const errorMessage = e.response?.data?.errors[0]?.error || 'An error occurred'
    toast.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 3000 })
  }
}

const onFormSubmit = ({ values, valid }: any) => {
  if (!valid) {
    return
  }
  confirm.require({
    message: 'Bạn có muốn tạo chiến dịch không?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: () => {
      requestCreate(values)
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}

const packages = ref([])

const requestFetchPackage = async () => {
  try {
    const res = await APIClient.get(CAMPAIGN_API.packages)
    const { data } = res.data
    if (res.data.code === 200) {
      packages.value = data
    }
  } catch (e: any) {
    console.log(e)
  }
}

onMounted(() => {
  requestFetchPackage()
})

const totalDay = ref(0)
watch(
  _date,
  (newValue: any) => {
    formRef.value?.setFieldValue('activated_at', newValue[0]);
    formRef.value?.setFieldValue('expired_at', newValue[1]);
    if (newValue[0] && newValue[1]) {
      totalDay.value = (
        moment(newValue[1]).diff(moment(newValue[0]), 'days') + 1
      )
    }
  }
)


</script>

<template>
  <div class="pt-3">
    <Form
      ref="formRef"
      v-slot="$form"
      :initial-values="initialValues"
      :resolver
      @submit="onFormSubmit"
      class="flex flex-col w-full"
    >
      <div class="flex flex-col flex-3">
        <div class="flex mb-3 gap-3">
          <div class="flex-1 text-right">Chọn gói:</div>
          <div class="flex-2">
            <Select
              name="package"
              :options="packages.map((e: any) => e.name)"
              placeholder="Chọn gói"
              class="form-control"
            />
            <span class="font-italic text-[12px] text-red-500">
              {{ $form?.package?.error?.message }}
            </span>
          </div>
        </div>
        <div class="flex mb-3 gap-3">
          <div class="flex-1 text-right">Thời gian:</div>
          <div class="flex-2">
            <DatePicker
              selectionMode="range"
              :manual-input="true"
              v-model="_date"
              class="form-control w-full"
              :minDate="new Date()"
            />
            <p class="font-italic text-[12px] text-red-500">
              {{ $form?.activated_at?.error?.message || $form?.expired_at?.error?.message }}
            </p>
            <InputText hidden name="activated_at" type="text" class="form-control" />
            <InputText hidden name="expired_at" type="text" class="form-control" />
          </div>
        </div>
        <div class="flex flex-col mb-3 gap-3 bg-blue-50 p-3 px-5" v-if="$form.package?.value">
          <div>
            <p>
              Gói <span class="font-bold">{{$form.package?.value}}</span>
              với chi phí <span class="font-bold">{{ numHelper(packages.find(e => e.name === $form.package?.value)?.price || 0).format() }}<sup>đ</sup></span> / ngày
            </p>
          </div>
          <template v-if="totalDay">
            <div>
              Thời hạn <span class="font-bold">{{totalDay}}</span> ngày
            </div>
            <div>
              Tổng chi phí <span class="font-bold">{{numHelper(packages.find(e => e.name === $form.package?.value)?.price * totalDay || 0).format()}}<sup>đ</sup></span>
            </div>
          </template>
        </div>
        <div class="flex mb-3 gap-3">
          <div class="flex-1 text-right"></div>
          <div class="flex-2">
            <button type="submit" class="btn btn-cs primary lg">Lưu</button>
          </div>
        </div>
      </div>
    </Form>
  </div>
</template>
